import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import {
  PaymentOrderType,
  PaymentSessionSnapshot,
  formatCountdown,
  getPaymentRemainingMs,
  readPaymentSessionSnapshot,
  resolvePaymentExpireTime
} from '../../shared/utils/payment-flow.util';

type DisplayStatus = 'processing' | 'success' | 'failed' | 'expired';

interface PaymentResultContext {
  orderType: PaymentOrderType;
  paymentResult: 'success' | 'failed';
  tenantId: string;
  aipoolTenantId: string;
  orderId: string;
  targetId: string;
  sourcePath: string;
}

@Component({
  selector: 'order-payment-result',
  standalone: true,
  imports: [CommonModule, I18nPipe, NzButtonModule, NzCardModule, NzIconModule, NzSpinModule, TestIdDirective],
  templateUrl: './order-payment-result.component.html',
  styleUrls: ['./order-payment-result.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderPaymentResultComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  loading = true;
  refreshing = false;
  displayStatus: DisplayStatus = 'processing';
  countdownText = '';
  expireTime: number | null = null;
  pollAttempts = 0;
  itemData: any = null;
  context: PaymentResultContext = {
    orderType: 'robot',
    paymentResult: 'success',
    tenantId: '',
    aipoolTenantId: '',
    orderId: '',
    targetId: '',
    sourcePath: ''
  };
  private sessionSnapshot: PaymentSessionSnapshot | null = null;
  private pollTimer: number | null = null;
  private countdownTimer: number | null = null;
  private paymentFinishedNotified = false;

  ngOnInit(): void {
    this.context = this.buildContext();
    this.sessionSnapshot = readPaymentSessionSnapshot(this.context.orderType, this.context.orderId);
    if (!this.context.targetId && this.sessionSnapshot?.targetId) {
      this.context.targetId = this.sessionSnapshot.targetId;
    }
    if (!this.context.tenantId && this.sessionSnapshot?.tenantId) {
      this.context.tenantId = this.sessionSnapshot.tenantId;
    }
    if (!this.context.aipoolTenantId && this.sessionSnapshot?.aipoolTenantId) {
      this.context.aipoolTenantId = this.sessionSnapshot.aipoolTenantId;
    }
    this.refreshState(true);
    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopCountdown();
  }

  getTitleKey(): string {
    switch (this.displayStatus) {
      case 'success':
        return 'payment.result.title.success';
      case 'failed':
        return 'payment.result.title.failed';
      case 'expired':
        return 'payment.result.title.expired';
      default:
        return 'payment.result.title.processing';
    }
  }

  getDescriptionKey(): string {
    switch (this.displayStatus) {
      case 'success':
        return 'payment.result.desc.success';
      case 'failed':
        return 'payment.result.desc.failed';
      case 'expired':
        return 'payment.result.desc.expired';
      default:
        return 'payment.result.desc.processing';
    }
  }

  getStatusIcon(): string {
    switch (this.displayStatus) {
      case 'success':
        return 'check-circle';
      case 'failed':
      case 'expired':
        return 'close-circle';
      default:
        return 'sync';
    }
  }

  getStatusIconClass(): string {
    return `status-icon-${this.displayStatus}`;
  }

  getSyncStatusKey(): string {
    switch (this.displayStatus) {
      case 'success':
        return 'payment.result.sync.success';
      case 'failed':
        return 'payment.result.sync.failed';
      case 'expired':
        return 'payment.result.sync.expired';
      default:
        return 'payment.result.sync.processing';
    }
  }

  getScopeLabel(): string {
    if (this.context.orderType === 'assistant') {
      return this.i18n.fanyi('payment.result.scope.assistant');
    }
    if (this.context.orderType === 'point') {
      return this.i18n.fanyi('payment.result.scope.point-package');
    }
    if (this.context.orderType === 'retention') {
      return '保存期限延长包';
    }
    if (this.context.orderType === 'quota-addon') {
      return this.i18n.fanyi('payment.result.scope.quota-addon');
    }
    return this.i18n.fanyi('payment.result.scope.robot');
  }

  getOrderIdText(): string {
    return this.context.orderId || '-';
  }

  getExpireTimeText(): string {
    if (!this.expireTime) return '-';
    return new Date(this.expireTime).toLocaleString();
  }

  canRetryRefresh(): boolean {
    return !!this.context.orderId && !!this.context.tenantId;
  }

  retryRefresh(): void {
    this.refreshState(true);
  }

  private refreshState(resetPolling: boolean): void {
    if (resetPolling) {
      this.pollAttempts = 0;
      this.stopPolling();
    }
    this.loading = resetPolling;
    this.refreshing = !resetPolling;

    if (!this.context.orderId || !this.context.tenantId) {
      this.applyDisplayStatus();
      this.loading = false;
      this.refreshing = false;
      this.cdr.markForCheck();
      return;
    }

    this.http
      .post(this.getOrderItemApiPath(), this.buildOrderItemPayload())
      .pipe(
        finalize(() => {
          this.loading = false;
          this.refreshing = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (isSuccess) {
            this.itemData = res?.data ?? null;
          }
          this.applyDisplayStatus();
          this.schedulePollingIfNeeded();
        },
        error: () => {
          this.applyDisplayStatus();
          this.schedulePollingIfNeeded();
        }
      });
  }

  private applyDisplayStatus(): void {
    const merged = this.getMergedData();
    this.expireTime = resolvePaymentExpireTime(merged);
    const remainingMs = getPaymentRemainingMs(merged);

    if (this.isPaid(merged)) {
      this.completeDisplayStatus('success');
      return;
    }

    if (this.isFailed(merged)) {
      this.completeDisplayStatus('failed');
      return;
    }

    if (this.isExpiredStatus(merged) || remainingMs === 0) {
      this.completeDisplayStatus('expired');
      return;
    }

    if (!this.isPaymentInProgress(merged)) {
      this.completeDisplayStatus(this.context.paymentResult === 'failed' ? 'failed' : 'success');
      return;
    }

    this.countdownText = remainingMs === null ? '' : formatCountdown(remainingMs);
    this.displayStatus = this.context.paymentResult === 'failed' ? 'failed' : 'processing';
    if (this.displayStatus !== 'processing') {
      this.completeDisplayStatus(this.displayStatus);
    }
  }

  private schedulePollingIfNeeded(): void {
    if (!this.context.orderId || !this.context.tenantId) return;
    if (this.displayStatus !== 'processing') return;
    if (this.pollAttempts >= 24) return;

    this.stopPolling();
    this.pollAttempts += 1;
    this.pollTimer = window.setTimeout(() => this.refreshState(false), 5000);
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.countdownTimer = window.setInterval(() => {
      const merged = this.getMergedData();
      const remainingMs = getPaymentRemainingMs(merged);
      this.countdownText = remainingMs === null ? '' : formatCountdown(remainingMs);
      if (remainingMs === 0 && this.displayStatus === 'processing') {
        this.completeDisplayStatus('expired');
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private stopCountdown(): void {
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private completeDisplayStatus(status: Exclude<DisplayStatus, 'processing'>): void {
    this.displayStatus = status;
    this.countdownText = '';
    this.stopPolling();
    this.stopCountdown();
    this.notifyPaymentFinished(status);
  }

  private notifyPaymentFinished(status: Exclude<DisplayStatus, 'processing'>): void {
    if (this.paymentFinishedNotified) return;
    this.paymentFinishedNotified = true;
    window.parent.postMessage(
      {
        source: 'aile-admin',
        type: 'payment.finished',
        payload: {
          orderType: this.context.orderType,
          orderId: this.context.orderId,
          targetId: this.context.targetId,
          status
        }
      },
      window.location.origin
    );
  }

  private getOrderItemApiPath(): string {
    if (this.context.orderType === 'assistant') {
      return ConstantUtil.ADMIN_ASSISTANT_ORDER_V1_ITEM;
    }
    if (this.context.orderType === 'point') {
      return ConstantUtil.ADMIN_TENANT_POINT_PACKAGE_ORDER_V1_ITEM.replace('{orderId}', encodeURIComponent(this.context.orderId));
    }
    if (this.context.orderType === 'retention' || this.context.orderType === 'quota-addon') {
      return ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_ITEM.replace('{orderId}', encodeURIComponent(this.context.orderId));
    }
    return ConstantUtil.ADMIN_ROBOT_ORDER_V1_ITEM;
  }

  private buildOrderItemPayload(): Record<string, string> {
    const payload: Record<string, string> = {};
    if (this.context.orderType !== 'point') {
      payload['orderId'] = this.context.orderId;
      payload['tenantId'] = this.context.tenantId;
    }
    const aipoolTenantId = String(this.context.aipoolTenantId || this.sessionSnapshot?.aipoolTenantId || '').trim();
    if (aipoolTenantId) {
      payload['aipoolTenantId'] = aipoolTenantId;
    }
    return payload;
  }

  private getMergedData(): any {
    return {
      ...(this.sessionSnapshot ?? {}),
      ...(this.itemData ?? {})
    };
  }

  private buildContext(): PaymentResultContext {
    const queryMap = this.route.snapshot.queryParamMap;
    const rawOrderType = String(queryMap.get('orderType') ?? 'robot')
      .trim()
      .toLowerCase();
    const orderType: PaymentOrderType =
      rawOrderType === 'assistant'
        ? 'assistant'
        : rawOrderType === 'retention'
          ? 'retention'
          : rawOrderType === 'quota-addon'
            ? 'quota-addon'
            : rawOrderType === 'point'
              ? 'point'
              : 'robot';
    const rawPaymentResult = String(queryMap.get('paymentResult') ?? 'success')
      .trim()
      .toLowerCase();
    const paymentResult: 'success' | 'failed' = rawPaymentResult === 'failed' ? 'failed' : 'success';

    return {
      orderType,
      paymentResult,
      tenantId: String(queryMap.get('tenantId') ?? '').trim(),
      aipoolTenantId: String(queryMap.get('aipoolTenantId') ?? '').trim(),
      orderId: String(queryMap.get('orderId') ?? '').trim(),
      targetId: String(queryMap.get('targetId') ?? '').trim(),
      sourcePath: String(queryMap.get('sourcePath') ?? '').trim()
    };
  }

  private isPaid(raw?: any): boolean {
    const status = this.getNormalizedStatus(raw?.status);
    if (['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(status)) return true;
    if (this.getStatusNumber(raw?.status) === 30) return true;

    const orderStatus = this.getNormalizedStatus(raw?.latestOrderStatus ?? raw?.orderStatus);
    if (['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(orderStatus)) return true;
    if (this.getStatusNumber(raw?.latestOrderStatus ?? raw?.orderStatus) === 30) return true;

    const paymentStatus = String(raw?.paymentStatus ?? '')
      .trim()
      .toLowerCase();
    if (['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(paymentStatus)) return true;
    if (this.getStatusNumber(raw?.paymentStatus) === 20) return true;

    const needPay = raw?.needPay;
    return needPay === false && !!raw?.payExpireTime;
  }

  private isFailed(raw?: any): boolean {
    return this.getStatusValues(raw).some(status =>
      ['failed', 'fail', 'error', 'canceled', 'cancelled', 'closed', 'rejected', 'voided', 'refunded'].includes(status)
    );
  }

  private isExpiredStatus(raw?: any): boolean {
    return this.getStatusValues(raw).some(status => ['expired', 'expire', 'timeout', 'timedout', 'time_out'].includes(status));
  }

  private isPaymentInProgress(raw?: any): boolean {
    const statuses = this.getStatusValues(raw);
    if (!statuses.length) return true;
    return statuses.some(status =>
      ['pending', 'unpaid', 'paying', 'processing', 'created', 'initialized', 'initiated', 'waiting', 'open', '10'].includes(status)
    );
  }

  private getStatusValues(raw?: any): string[] {
    return [raw?.status, raw?.latestOrderStatus, raw?.orderStatus, raw?.paymentStatus]
      .map(value => this.getNormalizedStatus(value))
      .filter(Boolean);
  }

  private getNormalizedStatus(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private getStatusNumber(value: unknown): number | null {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return parsed;
  }
}
